import type { EmailTemplateCategory } from '@/types';

export const APP_NAME = import.meta.env.VITE_APP_NAME ?? 'AdvisorKhoj-CRM';

// SYNC: server/src/constants/index.ts#ADD_ARN_NEEDS_AUM_MESSAGE - the two must
// match CHARACTER FOR CHARACTER, and nothing but this note enforces it.
//
// Both the quote guard and the order-selection guard throw that sentence, and
// LeadWizard decides whether to stay quiet about a refusal by comparing the
// server's message against this copy. Reword one side only and the rule starts
// being toasted twice per keystroke again, with nothing failing to say so.
export const ADD_ARN_NEEDS_AUM_MESSAGE =
  'Additional ARNs need an MFBOX plan (Silver/Gold) to attach to. Add one, or remove Additional ARN.';

export const ROUTES = {
  LANDING: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  // Landing page for an approved account whose jobRole maps to no workspace
  // (a legacy registration, or a role an admin has since retired). Replaces the
  // old placeholder /dashboard page, which every new employee used to land on.
  NO_WORKSPACE: '/no-workspace',
  PROFILE: '/profile',
  ONBOARDING: '/onboarding',
  ADMIN: '/admin',
  ADMIN_OVERVIEW: '/admin',
  ADMIN_PROFILE: '/admin/profile',
  APPROVALS: '/admin/approvals',
  ADMIN_USERS: '/admin/users',
  ADMIN_SESSIONS: '/admin/sessions',
  ADMIN_PINCODES: '/admin/pincodes',
  ADMIN_CUSTOM_CITIES: '/admin/custom-cities',
  ADMIN_EMAIL_TEMPLATES: '/admin/email-templates',
  ADMIN_EMAIL_TEMPLATE_NEW: '/admin/email-templates/new',
  ADMIN_EMAIL_TEMPLATE_EDIT: (id: number | string) => `/admin/email-templates/${id}/edit`,
  ADMIN_EMAIL_LOGS: '/admin/email-logs',
  ADMIN_TC_DOCUMENTS: '/admin/tc-documents',
  ADMIN_COMPANY_SETTINGS: '/admin/company-settings',
  ADMIN_PRICE_SETTINGS: '/admin/price-settings',
  ADMIN_SLABS: '/admin/slabs',
  ADMIN_MIGRATION: '/admin/migration',
  ADMIN_DEV: '/admin/dev',
  ADMIN_USER_DETAIL: (id: number | string) => `/admin/users/${id}`,
  // MIS_work_1 (Person 1): MIS Business Analytics page (staging, additive)
  ADMIN_MIS: '/admin/mis',
  // MIS_work_1 (Person 1): MIS Renewals page - Section A, panels A1-A8 (staging, additive)
  ADMIN_MIS_RENEWALS: '/admin/mis-renewals',
  // MIS_work_2 (Person 2): MIS Forecast page (staging, additive)
  ADMIN_MIS_FORECAST: '/admin/mis-forecast',
  // MIS_work_2 (Person 2): MIS-MFD Market Intelligence page (staging, additive)
  ADMIN_MIS_MFD: '/admin/mis-mfd',
  // Sales RM workspace
  SALES_RM: '/sales-rm',
  SALES_RM_HOME: '/sales-rm/home',
  SALES_RM_LEADS: '/sales-rm/leads',
  // Sales → Orders: one row per purchase (ADR-048), not one per client.
  SALES_RM_ORDERS: '/sales-rm/orders',
  SALES_RM_EMAIL_LOGS: '/sales-rm/email-logs',
  SALES_RM_TC_DOCUMENTS: '/sales-rm/tc-documents',
  SALES_RM_PROFILE: '/sales-rm/profile',
  // Billing workspace
  BILLING: '/billing',
  BILLING_DASHBOARD: '/billing/dashboard',
  BILLING_CLIENTS: '/billing/clients',
  // The queue is one row per ORDER (ADR-048), so the workflow link carries the
  // order alongside the client - otherwise a client with two open orders always
  // opens whichever one the server picks, not the row that was clicked.
  BILLING_WORKFLOW: (id: number | string, orderId?: number | string) =>
    orderId != null ? `/billing/clients/${id}?orderId=${orderId}` : `/billing/clients/${id}`,
  BILLING_EMAIL_LOGS: '/billing/email-logs',
  BILLING_PROFILE: '/billing/profile',
  // Renewals (live inside the Billing workspace, gated by BillingRoute)
  RENEWALS: '/billing/renewals',
  RENEWAL_WORKFLOW: (id: number | string) => `/billing/renewals/${id}`,
  RENEWAL_DASHBOARD: '/billing/renewal-dashboard',
  // Service workspace (gated by ServiceRoute)
  SERVICE: '/service',
  SERVICE_TASK: (id: number | string) => `/service/${id}`,
  SERVICE_PROFILE: '/service/profile',
  // Public onboarding pages reached from lead emails (no auth). :token = leadCode.
  REVIEW: '/review/:token',
  // Public Terms & Conditions acceptance page. :token = high-entropy T&C token.
  TC_PUBLIC: '/t/:token',
  NOT_FOUND: '*',
} as const;

// The jobRole that unlocks the Sales RM workspace (mirrors the server constant).
export const SALES_RM_ROLE = 'Sales RM';

// The jobRole that unlocks the Billing workspace (mirrors the server constant).
export const BILLING_ROLE = 'Billing';

// The jobRole that unlocks the Service workspace (mirrors the server constant).
export const SERVICE_ROLE = 'Service';

// Retired from the Request Access dropdown: Renewals and Billing are one desk.
// Accounts registered under the old option keep the value, so routing and the
// Billing guard still have to recognise it (mirrors the server RENEWAL_ROLE).
export const RENEWAL_ROLE = 'Renewal';

// Service task statuses (mirrors server SERVICE_TASK_STATUS).
export const SERVICE_TASK_STATUSES = [
  'Not Picked Up',
  'In Progress',
  'On Hold',
  'Blocked',
  'Completed',
] as const;

export type ServiceTaskStatusValue = (typeof SERVICE_TASK_STATUSES)[number];

// Service task source (mirrors server SERVICE_TASK_SOURCE). 'deactivation' and
// 'reactivation' are raised by the Renewals desk and, unlike 'billing', create
// no renewal row when completed (ADR-054).
export const SERVICE_TASK_SOURCES = ['billing', 'renewal', 'deactivation', 'reactivation'] as const;

export type ServiceTaskSourceValue = (typeof SERVICE_TASK_SOURCES)[number];

// Billing workflow statuses (mirrors server BILLING_STATUS). Order matters: it
// is the step order and the funnel/queue ordering. 'Partially Billed' is a
// lead-level derived status (all handed-over installments billed, but more are
// still due) - green, per ADR-042.
export const BILLING_STATUSES = [
  'Pending Verification',
  'Invoice Pending',
  'Service Notification Pending',
  'Client Invoice Pending',
  'Partially Billed',
  'Billing Complete',
] as const;

export type BillingStatusValue = (typeof BILLING_STATUSES)[number];

// Renewal workflow statuses (mirrors server RENEWAL_STATUS_VALUES). Order is
// the email-ladder / workflow ladder order used for filters and the funnel.
export const RENEWAL_STATUSES = [
  'Pending Verification',
  'Client Details Confirmed',
  'Renewal Notice Scheduled',
  'Renewal Notice Sent',
  'Follow-up 1 Sent',
  'Follow-up 2 Sent',
  'Invoice Pending',
  'Service Notification Pending',
  'Client Invoice Pending',
  'Renewal Complete',
  'Service Terminated',
  'Deactivated',
  'Skipped',
] as const;

export type RenewalStatusValue = (typeof RENEWAL_STATUSES)[number];

// How often a client's product subscription renews (mirrors the server
// RENEWAL_FREQUENCIES constant). Drives the per-period billed amount.
export const RENEWAL_FREQUENCIES = ['Annual', 'Quarterly', 'Monthly'] as const;

export type RenewalFrequencyValue = (typeof RENEWAL_FREQUENCIES)[number];

// Why a subscription was cancelled (mirrors the server
// RENEWAL_CANCELLATION_REASON constant). 'Renewal Unpaid' is written by the
// automated termination ladder and 'Upgraded To A Higher Plan' by the upgrade
// flow, so neither is offered in the picker; 'Other' requires the biller to
// type their own reason.
export const RENEWAL_CANCELLATION_REASONS = [
  'Renewal Unpaid',
  'Too Expensive',
  'Switched To Competitor',
  'Not Using The Service',
  'Service Quality Issue',
  'Business Closed / ARN Surrendered',
  'Upgraded To A Higher Plan',
  'Other',
] as const;

export type RenewalCancellationReasonValue = (typeof RENEWAL_CANCELLATION_REASONS)[number];

// The one value from RENEWAL_CANCELLATION_REASONS that is written by the
// automated termination ladder rather than picked by a biller (mirrors the
// server RENEWAL_CANCELLATION_REASON.RENEWAL_UNPAID member). Named so client
// code that tests for it does not repeat the bare string.
export const RENEWAL_CANCELLATION_REASON = {
  RENEWAL_UNPAID: 'Renewal Unpaid',
} as const;

// What a biller may pick by hand.
export const BILLER_CANCELLATION_REASONS: readonly RenewalCancellationReasonValue[] = [
  'Too Expensive',
  'Switched To Competitor',
  'Not Using The Service',
  'Service Quality Issue',
  'Business Closed / ARN Surrendered',
  'Other',
];

// Mirrors server EMAIL_TEMPLATE_CATEGORIES (constants/index.ts).
export const EMAIL_TEMPLATE_CATEGORY_OPTIONS: { value: EmailTemplateCategory; label: string }[] = [
  { value: 'sales', label: 'Sales' },
  { value: 'billing', label: 'Billing' },
  { value: 'renewal', label: 'Renewal' },
  { value: 'service', label: 'Service' },
  { value: 'internal', label: 'Internal' },
];

interface RoutableUser {
  role?: string;
  jobRole?: string | null;
}

// Where a freshly authenticated user lands by default. Sales RM jobRole takes
// priority so a user carrying both admin role and Sales RM jobRole lands on the
// Sales RM workspace (their primary daily tool). A deep-link `from` target
// (captured by ProtectedRoute) still takes priority over this default.
/**
 * The department an employee lands in, decided by the job role they requested on
 * the Request Access form.
 *
 * A CLIENT MIRROR of the server's JOB_ROLE_DEPARTMENT
 * (server/src/constants/index.ts), which is the authority - the department is
 * derived and stored there, and no client payload can set it. This copy exists
 * only so the onboarding form can SHOW an employee what they are being onboarded
 * into. There is no shared package between the workspaces, so it is
 * hand-maintained; a drift here is a wrong label, never a wrong write.
 *
 * `undefined` for a user with no jobRole (a registration predating the field) -
 * the form shows a dash rather than inventing a department.
 */
const JOB_ROLE_DEPARTMENT: Record<string, string> = {
  'Sales RM': 'Sales',
  Billing: 'Billing',
  Service: 'Service',
  // Retired from the Request Access dropdown - Renewals is worked by the Billing
  // desk. Kept here so an account registered under the old option still resolves
  // to a department instead of showing a dash.
  Renewal: 'Renewal',
  Admin: 'Admin',
  'Super Admin': 'Admin',
};

export const departmentForJobRole = (jobRole?: string | null): string | undefined =>
  jobRole ? JOB_ROLE_DEPARTMENT[jobRole] : undefined;

export const homeRouteFor = (user?: RoutableUser | null): string => {
  if (user?.jobRole === SALES_RM_ROLE) return ROUTES.SALES_RM_HOME;
  // Renewals is worked by the Billing desk, so a legacy 'Renewal' account lands
  // in the same workspace as a Billing one.
  if (user?.jobRole === BILLING_ROLE || user?.jobRole === RENEWAL_ROLE) {
    return ROUTES.BILLING_DASHBOARD;
  }
  if (user?.jobRole === SERVICE_ROLE) return ROUTES.SERVICE;
  if (user?.role === 'admin') return ROUTES.ADMIN;
  return ROUTES.NO_WORKSPACE;
};

export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  USER: 'auth_user',
  // sessionStorage flag set by apiClient right before a hard-redirect to
  // /login on forced logout, since a full page reload can't carry React state.
  SESSION_EXPIRED: 'ak_session_expired',
  // Manual collapse/expand of the admin console's sidebar, independent of
  // viewport breakpoint - persisted so it survives a reload.
  ADMIN_NAV_COLLAPSED: 'ak_admin_nav_collapsed',
  // Same, for the general workspace sidebar (MainLayout).
  MAIN_NAV_COLLAPSED: 'ak_main_nav_collapsed',
} as const;

export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 10,
  MAX_LIMIT: 1000,
} as const;

export const QUERY_KEYS = {
  AUTH: {
    ME: ['auth', 'me'] as const,
  },
  USERS: {
    ALL: ['users'] as const,
    BY_ID: (id: number) => ['users', id] as const,
    PENDING: ['users', 'pending'] as const,
  },
  SESSIONS: {
    MINE: ['sessions', 'mine'] as const,
  },
  ADMIN: {
    SESSIONS: ['admin', 'sessions'] as const,
    RM_PINCODES: ['admin', 'rm-pincodes'] as const,
  },
  EMAIL_TEMPLATES: {
    ALL: ['email-templates'] as const,
    BY_ID: (id: number) => ['email-templates', id] as const,
    VARIABLES: ['email-templates', 'variables'] as const,
  },
  EMAIL_PREVIEW: {
    BY_LEAD: (templateSlug: string, leadId: number) =>
      ['email-preview', templateSlug, leadId] as const,
  },
  COMPANY_SETTINGS: {
    ALL: ['company-settings'] as const,
    FINANCIAL_YEAR: ['company-settings', 'financial-year'] as const,
    BANK_ACCOUNTS: ['company-settings', 'bank-accounts'] as const,
  },
  PRODUCTS: {
    ALL: ['products'] as const,
  },
  PRICING: {
    CONFIG: ['pricing', 'config'] as const,
    SLABS: ['pricing', 'slabs'] as const,
  },
  PRICE_SETTINGS: {
    ALL: ['price-settings'] as const,
  },
  LEADS: {
    // Prefix used to invalidate every cached lead query at once.
    ALL: ['leads'] as const,
    LIST: ['leads', 'list'] as const,
    BY_ID: (id: number) => ['leads', id] as const,
    TC_STATUS: (id: number) => ['leads', id, 'tc-status'] as const,
  },
  // Purchases made by a client (ADR-048).
  ORDERS: {
    ROOT: ['orders'] as const,
    LIST: ['orders', 'list'] as const,
    BY_ID: (id: number) => ['orders', id] as const,
    FOR_LEAD: (leadId: number) => ['lead-orders', leadId] as const,
    SUBSCRIPTIONS: (leadId: number) => ['lead-subscriptions', leadId] as const,
    UPGRADE_OPTIONS: (leadId: number) => ['lead-upgrade-options', leadId] as const,
  },
  BILLING: {
    // Prefix used to invalidate every cached billing query at once.
    ROOT: ['billing'] as const,
    CLIENTS: ['billing', 'clients'] as const,
    LIST: ['billing', 'clients', 'list'] as const,
    BY_ID: (id: number) => ['billing', 'clients', id] as const,
    METRICS: ['billing', 'metrics'] as const,
    DASHBOARD: ['billing', 'dashboard'] as const,
  },
  RENEWALS: {
    // Prefix used to invalidate every cached renewals query at once.
    ROOT: ['renewals'] as const,
    LIST: ['renewals', 'list'] as const,
    BY_ID: (id: number) => ['renewals', id] as const,
    METRICS: ['renewals', 'metrics'] as const,
    DASHBOARD: ['renewals', 'dashboard'] as const,
    CALENDAR: ['renewals', 'calendar'] as const,
    ZERO_AUM_HOLDS: ['renewals', 'zero-aum-holds'] as const,
  },
  SERVICE: {
    ROOT: ['service'] as const,
    LIST: ['service', 'list'] as const,
    BY_ID: (id: number) => ['service', id] as const,
    METRICS: ['service', 'metrics'] as const,
  },
  SALES: {
    CLIENTS: ['sales', 'clients'] as const,
    // Prefix used to invalidate every cached dashboard query (metrics + pending).
    DASHBOARD_ROOT: ['sales', 'dashboard'] as const,
    DASHBOARD_METRICS: (from: string, to: string) =>
      ['sales', 'dashboard', 'metrics', from, to] as const,
    DASHBOARD_PENDING: (from: string, to: string) =>
      ['sales', 'dashboard', 'pending', from, to] as const,
    // Fixed current-period target progress; under DASHBOARD_ROOT so lead
    // mutations invalidate it too. Keyed by the month/year start so it refreshes
    // when the period rolls over (the starts are stable within a session).
    TARGET_PROGRESS: (monthStart: string, yearStart: string) =>
      ['sales', 'dashboard', 'target-progress', monthStart, yearStart] as const,
  },
} as const;
